import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import {
  denormalisedEntities,
  denormalisedResponseEntities,
  updatedEntities,
} from '../../util/data';
import { parse, getValidInboxSort } from '../../util/urlHelpers';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { sanitizeTransactionQueryResponse } from '../../util/transactionVisibility';
import {
  createParticipantUnavailableError,
  hasUnavailableTransactionParticipant,
  splitTransactionsByParticipantAvailability,
  unavailableParticipantLogData,
} from '../../util/participantAvailability';
import {
  BOOKING_PROCESS_NAME,
  INQUIRY_PROCESS_NAME,
  NEGOTIATION_PROCESS_NAME,
  PURCHASE_PROCESS_NAME,
  getProcess,
  getTransactionProcess,
  getTransactionProcessName,
} from '../../transactions/transaction';
import { getTransitionCandidateNames } from '../../transactions/transitionAliases';
import { ORDER_STATUS_GROUPS } from '../../util/orderStatusGroups';
import { getOrderSearchText } from './orderCardData';

export const INBOX_PAGE_SIZE = 20;
// The status tabs on the orders list, after "All"
export const INBOX_STATUS_TABS = ['toShip', 'shipped', 'delivered', 'completed', 'canceled'];
// A search looks through this many of the newest orders
const SEARCH_PAGE_SIZE = 100;
const SEARCH_MAX_PAGES = 5;

// ================ Helper functions ================ //

const uniqueValues = values => Array.from(new Set(values.filter(Boolean)));
const withReleaseAliases = processNames =>
  processNames.flatMap(processName =>
    processName.includes('/') ? [processName] : [processName, `${processName}/release-1`]
  );

export const INBOX_PROCESS_NAMES = uniqueValues(
  withReleaseAliases([
    PURCHASE_PROCESS_NAME,
    BOOKING_PROCESS_NAME,
    'default-buying-products',
    'flex-product-default-process',
    'offer-process',
    'wallet-process',
    'flex-default-process',
    'flex-hourly-default-process',
    'flex-booking-default-process',
    'booking-process',
  ])
).join(',');

const purchaseProcess = getProcess(PURCHASE_PROCESS_NAME);
const bookingProcess = getProcess(BOOKING_PROCESS_NAME);

const INBOX_VISIBLE_STATES_BY_PROCESS = {
  [PURCHASE_PROCESS_NAME]: new Set([
    purchaseProcess.states.PENDING_PAYMENT,
    purchaseProcess.states.PAYMENT_EXPIRED,
    purchaseProcess.states.PURCHASED,
    purchaseProcess.states.DELIVERED,
    purchaseProcess.states.RECEIVED_WITHOUT_PAYOUT,
    purchaseProcess.states.RECEIVED,
    purchaseProcess.states.DISPUTED,
    purchaseProcess.states.CANCELED,
    purchaseProcess.states.COMPLETED,
    purchaseProcess.states.WALLET_UPDATED,
    purchaseProcess.states.REVIEWED_BY_CUSTOMER,
    purchaseProcess.states.REVIEWED_BY_PROVIDER,
    purchaseProcess.states.REVIEWED,
  ]),
  [BOOKING_PROCESS_NAME]: new Set([
    bookingProcess.states.PENDING_PAYMENT,
    bookingProcess.states.PAYMENT_EXPIRED,
    bookingProcess.states.PREAUTHORIZED,
    bookingProcess.states.ACCEPTED,
    bookingProcess.states.DECLINED,
    bookingProcess.states.EXPIRED,
    bookingProcess.states.CANCELED,
    bookingProcess.states.DELIVERED,
    bookingProcess.states.REVIEWED_BY_CUSTOMER,
    bookingProcess.states.REVIEWED_BY_PROVIDER,
    bookingProcess.states.REVIEWED,
  ]),
};

const getInboxTransitionNames = (processName, visibleStates) => {
  const process = getProcess(processName);
  return uniqueValues(
    process
      .getTransitionsToStates(Array.from(visibleStates))
      .flatMap(transitionName => getTransitionCandidateNames(transitionName))
  );
};

export const INBOX_LAST_TRANSITIONS = uniqueValues([
  ...getInboxTransitionNames(
    PURCHASE_PROCESS_NAME,
    INBOX_VISIBLE_STATES_BY_PROCESS[PURCHASE_PROCESS_NAME]
  ),
  purchaseProcess.transitions.AUTO_CANCEL_WITHOUT_PAYOUT,
  ...getInboxTransitionNames(
    BOOKING_PROCESS_NAME,
    INBOX_VISIBLE_STATES_BY_PROCESS[BOOKING_PROCESS_NAME]
  ),
]);

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const mergeUniqueById = entities => {
  const entityMap = entities.reduce((acc, entity) => {
    const id = entity?.id?.uuid;
    if (!id) {
      return acc;
    }

    acc.set(id, entity);
    return acc;
  }, new Map());

  return Array.from(entityMap.values());
};

const withTransactions = (response, transactions) => ({
  ...response,
  data: {
    ...response?.data,
    data: transactions,
  },
});

const logHiddenUnavailableTransactions = hiddenTransactions => {
  hiddenTransactions.forEach(({ transaction, unavailableParticipant }) => {
    log.warn(
      createParticipantUnavailableError(),
      'conversation-hidden-unavailable-participant',
      unavailableParticipantLogData(transaction, unavailableParticipant)
    );
  });
};

const getInboxTransactionState = tx => {
  const processName = getTransactionProcessName(tx);
  if (!processName || [NEGOTIATION_PROCESS_NAME, INQUIRY_PROCESS_NAME].includes(processName)) {
    return { processName, processState: null };
  }

  try {
    const process = getTransactionProcess(tx);
    return {
      processName,
      processState: process.getState(tx),
    };
  } catch {
    return { processName, processState: null };
  }
};

export const isInboxVisibleTransactionState = tx => {
  const { processName, processState } = getInboxTransactionState(tx);
  const visibleStates = processName ? INBOX_VISIBLE_STATES_BY_PROCESS[processName] : null;
  return !!visibleStates?.has(processState);
};

export const isInboxVisibleTransaction = tx =>
  isInboxVisibleTransactionState(tx) && !hasUnavailableTransactionParticipant(tx);

// ================ Slice ================ //

const inboxPageSlice = createSlice({
  name: 'InboxPage',
  initialState: {
    fetchInProgress: false,
    fetchOrdersOrSalesError: null,
    pagination: null,
    transactionRefs: [],
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(loadDataThunk.pending, state => {
        state.fetchInProgress = true;
        state.fetchOrdersOrSalesError = null;
      })
      .addCase(loadDataThunk.fulfilled, (state, action) => {
        const transactions = action.payload.data.data;
        state.fetchInProgress = false;
        state.transactionRefs = entityRefs(transactions);
        state.pagination = action.payload.data.meta;
      })
      .addCase(loadDataThunk.rejected, (state, action) => {
        console.error(action.payload || action.error); // eslint-disable-line
        state.fetchInProgress = false;
        state.fetchOrdersOrSalesError = action.payload;
      });
  },
});

export default inboxPageSlice.reducer;

// ================ Load data ================ //

const loadDataPayloadCreator = ({ params, search }, { dispatch, rejectWithValue, extra: sdk }) => {
  const { tab } = params;

  const onlyFilterValues = {
    orders: 'order',
    sales: 'sale',
  };

  const onlyFilter = onlyFilterValues[tab];
  if (!onlyFilter) {
    return Promise.reject(new Error(`Invalid tab for InboxPage: ${tab}`));
  }

  const { page = 1, sort, status, q } = parse(search);
  const targetPage = Number(page) || 1;
  // parse() turns number-like values (a SKU, say) into numbers
  const searchText =
    q != null
      ? String(q)
          .trim()
          .toLowerCase()
      : '';
  // A status tab asks Sharetribe for just those statuses, so counts and pages cover every order
  const statusFilter = INBOX_STATUS_TABS.includes(status)
    ? { states: ORDER_STATUS_GROUPS[status].join(',') }
    : { lastTransitions: INBOX_LAST_TRANSITIONS };
  const apiQueryParams = {
    only: onlyFilter,
    processNames: INBOX_PROCESS_NAMES,
    ...statusFilter,
    include: [
      'listing',
      'listing.images',
      'provider',
      'provider.profileImage',
      'customer',
      'customer.profileImage',
      'booking',
    ],
    'fields.transaction': [
      'processName',
      'lastTransition',
      'lastTransitionedAt',
      'transitions',
      'payinTotal',
      'payoutTotal',
      'lineItems',
      'protectedData',
      'metadata',
    ],
    'fields.listing': ['title', 'publicData', 'description'],
    'fields.user': [
      'profile.displayName',
      'profile.abbreviatedName',
      'deleted',
      'banned',
      'state',
      'disabled',
    ],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
    perPage: INBOX_PAGE_SIZE,
    ...getValidInboxSort(sort),
  };

  const MAX_RETRIES = 2;
  const queryWithRetry = async (retriesLeft, extraParams = { page: targetPage }) => {
    try {
      return await sdk.transactions.query({ ...apiQueryParams, ...extraParams });
    } catch (e) {
      if (retriesLeft > 0) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        return queryWithRetry(retriesLeft - 1, extraParams);
      }
      throw e;
    }
  };

  // A search reads the newest orders (up to 500) and keeps the ones whose item, buyer or seller
  // matches; Sharetribe can't search orders by text itself
  const searchAllOrders = async () => {
    const pages = [];
    for (let searchPage = 1; searchPage <= SEARCH_MAX_PAGES; searchPage += 1) {
      const response = await queryWithRetry(MAX_RETRIES, {
        page: searchPage,
        perPage: SEARCH_PAGE_SIZE,
      });
      pages.push(response);
      if (searchPage >= (response?.data?.meta?.totalPages || 1)) {
        break;
      }
    }
    const sanitizedPages = pages.map(sanitizeTransactionQueryResponse);
    const transactions = sanitizedPages.flatMap(response => response?.data?.data || []);
    const included = sanitizedPages.flatMap(response => response?.data?.included || []);
    // Look up related records once; an order whose buyer, seller or listing is missing is
    // simply left out rather than failing the search
    const entities = updatedEntities({}, { data: transactions, included });
    const matching = transactions.filter(tx => {
      const [entity] = denormalisedEntities(entities, [{ id: tx.id, type: tx.type }], false);
      return (
        !!entity &&
        isInboxVisibleTransaction(entity) &&
        getOrderSearchText(entity).includes(searchText)
      );
    });
    const start = (targetPage - 1) * INBOX_PAGE_SIZE;
    return {
      data: {
        data: matching.slice(start, start + INBOX_PAGE_SIZE),
        included,
        meta: {
          page: targetPage,
          perPage: INBOX_PAGE_SIZE,
          totalItems: matching.length,
          totalPages: Math.max(1, Math.ceil(matching.length / INBOX_PAGE_SIZE)),
        },
      },
    };
  };

  return (searchText ? searchAllOrders() : queryWithRetry(MAX_RETRIES))
    .then(response => {
      const sanitizedResponse = sanitizeTransactionQueryResponse(response);
      const rawTransactions = sanitizedResponse?.data?.data || [];
      const stateVisibleTransactions = rawTransactions.filter(isInboxVisibleTransactionState);
      let denormalizedTransactions = [];

      try {
        denormalizedTransactions = denormalisedResponseEntities(
          withTransactions(sanitizedResponse, stateVisibleTransactions)
        );
      } catch (e) {
        log.warn(e, 'inbox-participant-denormalization-failed', {
          tab,
          page: targetPage,
        });
      }

      const {
        visibleTransactions: availableTransactions,
        hiddenTransactions,
      } = splitTransactionsByParticipantAvailability(
        stateVisibleTransactions,
        denormalizedTransactions,
        sanitizedResponse?.data?.included || []
      );
      logHiddenUnavailableTransactions(hiddenTransactions);

      const visibleTransactions = mergeUniqueById(availableTransactions);
      dispatch(addMarketplaceEntities(withTransactions(sanitizedResponse, visibleTransactions)));
      const responseMeta = sanitizedResponse?.data?.meta || {};
      const totalItems = Number(responseMeta.totalItems);
      const totalPages = Number(responseMeta.totalPages);
      const page = Number(responseMeta.page);
      const perPage = Number(responseMeta.perPage);

      return {
        data: {
          data: visibleTransactions,
          meta: {
            ...responseMeta,
            page: Number.isFinite(page) ? page : targetPage,
            perPage: Number.isFinite(perPage) ? perPage : INBOX_PAGE_SIZE,
            totalItems: Number.isFinite(totalItems) ? totalItems : visibleTransactions.length,
            totalPages: Number.isFinite(totalPages) ? totalPages : 1,
          },
        },
      };
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const loadDataThunk = createAsyncThunk('InboxPage/loadData', loadDataPayloadCreator);

// Backward compatible wrapper for the thunk
export const loadData = (params, search) => (dispatch, getState, sdk) => {
  return dispatch(loadDataThunk({ params, search }));
};

/**
 * How many orders are in each status tab, plus "all", for the signed-in user as buyer (tab
 * "orders") or seller (tab "sales"). A tab whose count fails is left out.
 */
export const fetchInboxCounts = tab => (dispatch, getState, sdk) => {
  if (!sdk?.transactions) {
    return Promise.resolve({});
  }
  const only = tab === 'sales' ? 'sale' : 'order';
  const filters = {
    all: { lastTransitions: INBOX_LAST_TRANSITIONS },
    ...Object.fromEntries(
      INBOX_STATUS_TABS.map(status => [status, { states: ORDER_STATUS_GROUPS[status].join(',') }])
    ),
  };
  return Promise.all(
    Object.entries(filters).map(([key, filter]) =>
      sdk.transactions
        .query({ only, processNames: INBOX_PROCESS_NAMES, ...filter, perPage: 1 })
        .then(response => [key, response?.data?.meta?.totalItems])
        .catch(() => [key, undefined])
    )
  ).then(entries => Object.fromEntries(entries.filter(([, count]) => Number.isInteger(count))));
};
